import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { FieldChangeEvent, SwitchChipGroup, Text } from '@frontend/design-system';

type DaysToSendProps = {
  disabled?: boolean;
  name: string;
  onChange: (e?: FieldChangeEvent) => void;
  trackingId?: string;
  value: string[];
};

const getToggledDay = (prevDays: string[], newDays: string[]): string | null => {
  if (newDays.length === prevDays.length + 1) {
    return newDays.find((day) => !prevDays.includes(day)) || null;
  }

  if (newDays.length === prevDays.length - 1) {
    return prevDays.find((day) => !newDays.includes(day)) || null;
  }

  return null;
};

export const SelectedDaysChips = ({ disabled, name, onChange, trackingId, value, ...rest }: DaysToSendProps) => {
  const { t } = useTranslation('bulk-messaging');
  const options = [
    { name: 'monday', label: t('Mon') },
    { name: 'tuesday', label: t('Tues') },
    { name: 'wednesday', label: t('Wed') },
    { name: 'thursday', label: t('Thurs') },
    { name: 'friday', label: t('Fri') },
    { name: 'saturday', label: t('Sat') },
    { name: 'sunday', label: t('Sun') },
  ];

  const setValue: Dispatch<SetStateAction<string[]>> = (action) => {
    const newDays = typeof action === 'function' ? action(value) : action;
    const toggledDay = getToggledDay(value, newDays);

    if (!toggledDay) return;

    const changeEvent: FieldChangeEvent = {
      name,
      value: toggledDay,
    };

    onChange(changeEvent);
  };

  return (
    <>
      <SwitchChipGroup
        css={{
          pointerEvents: 'auto',
          marginTop: theme.spacing(2),
          '> .chip': {
            height: theme.spacing(4),
          },
        }}
        disabled={disabled}
        value={value}
        setValue={setValue}
        checkboxMode
        {...rest}
      >
        {options.map(({ name, label }) => (
          <SwitchChipGroup.Option
            key={name}
            value={name}
            trackingId={trackingId ? `${trackingId}-${name}-checkbox` : undefined}
            isNew
          >
            {label}
          </SwitchChipGroup.Option>
        ))}
      </SwitchChipGroup>
      <Text hidden={!!value.length} color='error' size='small' css={{ paddingTop: theme.spacing(1) }}>
        {t('Please select at least one day to send')}
      </Text>
    </>
  );
};
